import type { World } from '@/ecs/World';
import type { Entity } from '@/ecs/Entity';
import type { AxialCoord } from '@/types/hex.types';
import { Position } from '@/ecs/Component';
import { Stats, Owner, CombatState } from '@/ecs/components/UnitComponents';
import { hexDistance, coordToKey } from '@/hex/HexCoord';

/**
 * Targeting strategies
 */
export enum TargetingStrategy {
  Closest = 'closest', // Target closest enemy
  LowestHealth = 'lowestHealth', // Target enemy with lowest current HP
  HighestHealth = 'highestHealth', // Target enemy with highest current HP
  LowestDefense = 'lowestDefense', // Target enemy with lowest defense (squishy)
  HighestDamage = 'highestDamage', // Target enemy with highest attack
  Random = 'random', // Random target
}

/**
 * Targeting system for combat
 */
export class TargetingSystem {
  private world: World;

  constructor(world: World) {
    this.world = world;
  }

  /**
   * Find a target for an attacking unit
   */
  findTarget(
    attacker: Entity,
    strategy: TargetingStrategy = TargetingStrategy.Closest
  ): Entity | null {
    const attackerOwner = attacker.getComponent(Owner);
    const attackerPos = attacker.getComponent(Position);

    if (!attackerOwner || !attackerPos) return null;

    // Get all valid enemy targets
    const enemies = this.getEnemyUnits(attackerOwner.playerId);

    if (enemies.length === 0) return null;

    switch (strategy) {
      case TargetingStrategy.Closest:
        return this.findClosestTarget(attackerPos, enemies);
      case TargetingStrategy.LowestHealth:
        return this.findLowestHealthTarget(enemies);
      case TargetingStrategy.HighestHealth:
        return this.findHighestHealthTarget(enemies);
      case TargetingStrategy.LowestDefense:
        return this.findLowestDefenseTarget(enemies);
      case TargetingStrategy.HighestDamage:
        return this.findHighestDamageTarget(enemies);
      case TargetingStrategy.Random:
        return this.findRandomTarget(enemies);
      default:
        return this.findClosestTarget(attackerPos, enemies);
    }
  }

  /**
   * Get all living enemy units
   */
  getEnemyUnits(playerId: number): Entity[] {
    return this.world
      .query(Position, Owner, Stats, CombatState)
      .filter((entity) => {
        const owner = entity.getComponent(Owner)!;
        const combatState = entity.getComponent(CombatState)!;
        return owner.playerId !== playerId && !combatState.isDead;
      });
  }

  /**
   * Get all living allied units
   */
  getAlliedUnits(playerId: number): Entity[] {
    return this.world
      .query(Position, Owner, Stats, CombatState)
      .filter((entity) => {
        const owner = entity.getComponent(Owner)!;
        const combatState = entity.getComponent(CombatState)!;
        return owner.playerId === playerId && !combatState.isDead;
      });
  }

  /**
   * Get all living units on the board
   */
  getAllUnitsOnBoard(): Entity[] {
    return this.world
      .query(Position, Owner, Stats, CombatState)
      .filter((entity) => {
        const owner = entity.getComponent(Owner)!;
        const combatState = entity.getComponent(CombatState)!;
        return !owner.isOnBench && !combatState.isDead;
      });
  }

  /**
   * Find closest enemy
   */
  private findClosestTarget(
    attackerPos: Position,
    enemies: Entity[]
  ): Entity | null {
    let closest: Entity | null = null;
    let minDistance = Infinity;

    for (const enemy of enemies) {
      const enemyPos = enemy.getComponent(Position)!;
      const distance = hexDistance(
        { q: attackerPos.q, r: attackerPos.r },
        { q: enemyPos.q, r: enemyPos.r }
      );

      if (distance < minDistance) {
        minDistance = distance;
        closest = enemy;
      }
    }

    return closest;
  }

  /**
   * Find enemy with lowest HP
   */
  private findLowestHealthTarget(enemies: Entity[]): Entity | null {
    let target: Entity | null = null;
    let lowestHP = Infinity;

    for (const enemy of enemies) {
      const stats = enemy.getComponent(Stats)!;
      if (stats.currentHealth < lowestHP) {
        lowestHP = stats.currentHealth;
        target = enemy;
      }
    }

    return target;
  }

  /**
   * Find enemy with highest HP
   */
  private findHighestHealthTarget(enemies: Entity[]): Entity | null {
    let target: Entity | null = null;
    let highestHP = -Infinity;

    for (const enemy of enemies) {
      const stats = enemy.getComponent(Stats)!;
      if (stats.currentHealth > highestHP) {
        highestHP = stats.currentHealth;
        target = enemy;
      }
    }

    return target;
  }

  /**
   * Find enemy with lowest defense
   */
  private findLowestDefenseTarget(enemies: Entity[]): Entity | null {
    let target: Entity | null = null;
    let lowestDef = Infinity;

    for (const enemy of enemies) {
      const stats = enemy.getComponent(Stats)!;
      if (stats.defense < lowestDef) {
        lowestDef = stats.defense;
        target = enemy;
      }
    }

    return target;
  }

  /**
   * Find enemy with highest damage
   */
  private findHighestDamageTarget(enemies: Entity[]): Entity | null {
    let target: Entity | null = null;
    let highestAtk = -Infinity;

    for (const enemy of enemies) {
      const stats = enemy.getComponent(Stats)!;
      if (stats.attack > highestAtk) {
        highestAtk = stats.attack;
        target = enemy;
      }
    }

    return target;
  }

  /**
   * Find random enemy
   */
  private findRandomTarget(enemies: Entity[]): Entity | null {
    if (enemies.length === 0) return null;
    const index = Math.floor(Math.random() * enemies.length);
    return enemies[index];
  }

  /**
   * Check if target is within attack range
   */
  isInAttackRange(attacker: Entity, target: Entity): boolean {
    const attackerPos = attacker.getComponent(Position);
    const attackerStats = attacker.getComponent(Stats);
    const targetPos = target.getComponent(Position);

    if (!attackerPos || !attackerStats || !targetPos) return false;

    const distance = hexDistance(
      { q: attackerPos.q, r: attackerPos.r },
      { q: targetPos.q, r: targetPos.r }
    );

    return distance <= attackerStats.attackRange;
  }

  /**
   * Get distance to target
   */
  getDistanceToTarget(attacker: Entity, target: Entity): number {
    const attackerPos = attacker.getComponent(Position);
    const targetPos = target.getComponent(Position);

    if (!attackerPos || !targetPos) return Infinity;

    return hexDistance(
      { q: attackerPos.q, r: attackerPos.r },
      { q: targetPos.q, r: targetPos.r }
    );
  }

  /**
   * Get all blocked coordinates (occupied hexes)
   */
  getBlockedCoords(): Set<string> {
    const blocked = new Set<string>();

    const units = this.getAllUnitsOnBoard();
    for (const unit of units) {
      const pos = unit.getComponent(Position);
      if (pos) {
        blocked.add(coordToKey({ q: pos.q, r: pos.r }));
      }
    }

    return blocked;
  }

  /**
   * Get units within range of a coordinate
   */
  getUnitsInRange(center: AxialCoord, range: number): Entity[] {
    return this.getAllUnitsOnBoard().filter((unit) => {
      const pos = unit.getComponent(Position)!;
      return hexDistance(center, { q: pos.q, r: pos.r }) <= range;
    });
  }

  /**
   * Get enemy units within range
   */
  getEnemiesInRange(
    center: AxialCoord,
    range: number,
    attackerPlayerId: number
  ): Entity[] {
    return this.getUnitsInRange(center, range).filter((unit) => {
      const owner = unit.getComponent(Owner)!;
      const combatState = unit.getComponent(CombatState)!;
      return owner.playerId !== attackerPlayerId && !combatState.isDead;
    });
  }
}
